import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';

import IwMedia from '../../../iwMedia/index';
import { SHOW_REPAYMENT_SCHEDULE_MODAL } from '../../modals/repaymentScheduleModal/RepaymentScheduleModal.actions';
import { MainContentBlockPending } from '../../pageStructure/mainContentBlockPending/MainContentBlockPending';
import { MainSectionWrapper } from '../../pageStructure/mainSectionWrapper';
import { TitleSectionWrapper } from '../../pageStructure/titleSectionWrapper/TitleSectionWrapper';
import { MoneyWithDecimals } from '../../ui/moneyWithDecimals';

import moneyStyles from '../../ui/moneyWithDecimals/MoneyWithDecimals.module.css';
import { NextRepaymentSummary } from '../../ui/nextRepaymentSummary/NextRepaymentSummary';
import PairItemContainer from '../../ui/pairItemContainer/PairItemContainer';
import {
    CONFIRM_EARLY_REPAYMENT,
    SELECT_REPAYMENT_METHOD,
    SHOW_CHOOSE_REPAYMENT_AMOUNT_STEP
} from '../EarlyRepayment.actions';
import {
    MANUAL_BANK_TRANSFER_OPTION,
    TRUELAYER_REPAYMENT_TYPE
} from '../EarlyRepaymentHelper';
import messages from './ConfirmRepayment.messages';
import styles from './ConfirmRepayment.module.css';
import { PaymentMethodInfo } from './PaymentMethodInfo';
import { SelectPaymentMethod } from './SelectPaymentMethod';
import { useStripe } from '@stripe/react-stripe-js';
import { RootState } from '@iwProvider/configureStore';
import { Dispatch } from 'redux';
import { TSelectedPayment } from '../EarlyRepayment.reducer';
import { Stripe } from '@stripe/stripe-js';
import { Text } from '../../ui/elements/Text/Text';
import { Button } from '@iwoca/orion';

const confirmRepaymentForm = {
    form: 'confirmRepayment',
    enableReinitialize: true
};

type TPaymentType = {
    type: string;
    label: string;
    issuer: string;
    brand: string;
    cardLastDigits: string;
    value: string;
} | null;

const mapStateToProps = (state: RootState) => ({
    ...state.earlyRepayment,
    initialValues: {
        payment: state.earlyRepayment.selectedPayment
    }
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    goToChooseAmountStep: () =>
        dispatch({ type: SHOW_CHOOSE_REPAYMENT_AMOUNT_STEP }),
    showSchedule: () => dispatch({ type: SHOW_REPAYMENT_SCHEDULE_MODAL }),
    selectRepaymentMethod: (selectedPayment: TSelectedPayment) =>
        dispatch({ type: SELECT_REPAYMENT_METHOD, selectedPayment }),
    confirmAction: (stripe: Stripe, paymentType: TPaymentType) =>
        dispatch({ type: CONFIRM_EARLY_REPAYMENT, stripe, paymentType })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

const ConfirmRepayment = ({
    ready,
    selectedInputAmount,
    remainingOutstandingBalance,
    goToChooseAmountStep,
    confirmAction,
    nextRepaymentAmount,
    nextRepaymentDate,
    previousNextRepaymentAmount,
    paymentOptions,
    selectRepaymentMethod,
    selectedPayment,
    disabledRepaymentMethodSelection,
    monthlyRepaymentsCounter,
    showSchedule
}: PropsFromRedux) => {
    const stripe = useStripe();
    if (!ready) {
        return <MainContentBlockPending />;
    }

    const shouldShowExtraInfo =
        selectedPayment?.type === TRUELAYER_REPAYMENT_TYPE;

    return (
        <div className={styles.ConfirmRepayment}>
            <IwMedia from="medium">
                <TitleSectionWrapper>
                    <div className={styles.title}>
                        <FormattedMessage {...messages.title} />
                    </div>
                </TitleSectionWrapper>
            </IwMedia>
            <MainSectionWrapper>
                <PairItemContainer>
                    {/* Left side */}
                    <div
                        className={styles.summaryContainer}
                        data-testid="repayment-summary"
                    >
                        <div className={styles.selectedAmount}>
                            <div className={styles.summaryTitle}>
                                <FormattedMessage {...messages.summaryTitle} />
                            </div>

                            <div>
                                <MoneyWithDecimals
                                    className={moneyStyles.MoneyWithDecimals}
                                    value={selectedInputAmount!}
                                    integerWithSymbolClassName={
                                        styles.repayMoneyIntegers
                                    }
                                    decimalWithSeparatorClassName={
                                        styles.repayMoneyDecimals
                                    }
                                />
                            </div>
                        </div>

                        <div>
                            <FormattedMessage
                                {...messages.summaryBreakdown}
                                values={{
                                    newBalance: (
                                        <FormattedCurrency
                                            value={remainingOutstandingBalance!}
                                        />
                                    )
                                }}
                            />
                        </div>
                    </div>

                    {/* Right side */}
                    <div className={styles.repaymentSummary}>
                        <NextRepaymentSummary
                            date={nextRepaymentDate}
                            amount={nextRepaymentAmount}
                            previousNextRepayment={previousNextRepaymentAmount}
                        />

                        {monthlyRepaymentsCounter ? (
                            <div className={styles.showSchedule}>
                                <span
                                    className={styles.showScheduleLink}
                                    onClick={showSchedule}
                                >
                                    <FormattedMessage
                                        {...messages.showSchedule}
                                    />
                                </span>
                            </div>
                        ) : null}
                    </div>
                </PairItemContainer>

                <div
                    className={classNames(
                        styles.choosePaymentContainer,
                        styles.choosePayment,
                        {
                            [styles.wFull]: shouldShowExtraInfo
                        }
                    )}
                >
                    {/* Left side */}
                    <div
                        className={classNames(
                            shouldShowExtraInfo
                                ? styles.choosePaymentLeftContainerWithExtraInfo
                                : styles.choosePaymentLeftContainer
                        )}
                    >
                        <div className={styles.choosePaymentTitle}>
                            {/* NB: The messages are different in UK and DE
                            because there is only option in DE */}
                            <FormattedMessage {...messages.paidBy} />
                        </div>
                        <form autoComplete="off">
                            <SelectPaymentMethod
                                name="payment"
                                options={paymentOptions}
                                onChange={selectRepaymentMethod}
                                disabled={disabledRepaymentMethodSelection}
                            />
                            {selectedPayment?.label ===
                                'Instant Bank Transfer' && (
                                <p className={styles.tideNote}>
                                    <FormattedMessage {...messages.tideNote} />
                                </p>
                            )}
                        </form>
                    </div>
                    {/* Right side */}
                    <div
                        className={classNames(
                            styles.choosePaymentRightContainer,
                            {
                                [styles.hidden]: !shouldShowExtraInfo
                            }
                        )}
                    >
                        <PaymentMethodInfo
                            selectedPaymentMethod={selectedPayment?.type!}
                        />
                    </div>
                </div>

                <div className="mb-[80px] mt-s box-border w-full pl-[32px] text-s md:mb-s lg:text-m">
                    <FormattedMessage
                        {...messages.showManualRepaymentDetailsQuestion}
                        values={{
                            link: (
                                <button
                                    className={styles.manualRepaymentLink}
                                    onClick={() => {
                                        confirmAction(
                                            stripe!,
                                            MANUAL_BANK_TRANSFER_OPTION
                                        );
                                    }}
                                >
                                    <FormattedMessage
                                        {...messages.showManualRepaymentDetailsLink}
                                    />
                                </button>
                            )
                        }}
                    />
                </div>

                <div className="fixed inset-x-0 bottom-0 z-10 bg-secondary-97">
                    <div className=" mx-auto box-border flex w-full max-w-[60rem] flex-col px-[32px] py-l md:mt-0 md:flex-row md:justify-between md:p-xl">
                        <Button
                            onClick={goToChooseAmountStep}
                            variant="secondary"
                            size="large"
                            className="md:mr-xl"
                        >
                            <FormattedMessage {...messages.back} />
                        </Button>

                        <div className="mt-xl flex flex-col-reverse md:mt-0 md:flex-row md:items-center">
                            {shouldShowExtraInfo && (
                                <Text className="mt-s text-s font-med md:mr-l md:mt-0 md:max-w-[300px]">
                                    You will be redirected to our payment
                                    partner, Truelayer, to complete the payment
                                </Text>
                            )}
                            <Button
                                onClick={() => {
                                    confirmAction(stripe!, null);
                                }}
                                size="large"
                            >
                                <FormattedMessage {...messages.next} />
                            </Button>
                        </div>
                    </div>
                </div>
            </MainSectionWrapper>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(reduxForm<any, PropsFromRedux>(confirmRepaymentForm)(ConfirmRepayment));
